// constants/disclosure.js
// CURATED disclosure metadata for the Disclosure page (handoff 10). Stable
// reference data: framework display names, families, notes, the TNFD-metric
// crosswalk, the route clauses, and the five honest-status colour matrix. The
// per-site STATUS and the live TNFD metric VALUES come from the published
// payload (summary.disclosure); these constants are the metadata join + the
// fallback rendered when nothing is published yet.
//
// 1:1 port of AppDevPromptsMngmnt/prototype/disclosure.html (Wilder Wood / WLD
// demonstration). Where the static mock and the RB UX diverge, the mock already
// reflects the canonical decision.
//
// CANON (hard acceptance criteria, not styling):
// - No em dashes anywhere. Commas / periods / colons / middot only.
// - "informed by" / "supports" / "prepared so a VVB can assess". NEVER
//   "compliant" / "in accordance with" / "certified" / "verified" / "assured".
// - Five honest statuses. EcoIntel ceiling = Evidenced + Documented. VVB is
//   OPTIONAL, EXTERNAL, BEYOND the ceiling, never implied as ours.
// - EDX, not EHI. Land Health cited as a 1-9 level + band, never % alone.
// - CDI = "Ecological Community Dynamics", never "Biodiversity", never a species count.
// - Lift moves Baseline -> Documented ONLY, never recolours satellite evidence
//   to Evidenced, never itself verifies.

// The five honest statuses: [label, bg, fg]. Literal-hex exception (the only one
// besides the EDX band hexes), mirroring how HelpDrawer already treats EDX_COLORS.
// NOTE: Positioned uses teal #0E7C86 / #E0F4F4 (RB-UX authoritative). Per handoff
// 10 acceptance the brand token --pos / --pos-bg now carries this same teal, so
// the token, this matrix and handoff 09's glossary agree; the earlier purple
// moved to --layer-nature / --nature-bg for the Reports "Supporting" group. The
// page keeps literal hexes so it renders identically regardless of the token.
export const ST = {
  evidenced: ['Evidenced', '#EAF3DE', '#3B6D11'],
  documented: ['Documented', '#E6F1FB', '#185FA5'],
  positioned: ['Positioned', '#E0F4F4', '#0E7C86'],
  baseline: ['Baseline', '#FAEEDA', '#854F0B'],
  none: ['Not assessed', '#ededed', '#777'],
};

// Per-status fill colour for the framework progress bars.
export const STATUS_FILL = {
  evidenced: '#3B6D11', documented: '#185FA5', positioned: '#0E7C86', baseline: '#854F0B',
};

// The curated frameworks, in canonical array order.
// `status` here is the FALLBACK; the live value comes from summary.disclosure.
// Group order as encountered: Nature disclosure, Carbon & climate, Regulatory readiness.
// UN SEEA leads Nature disclosure, above TNFD. Its account card is shown only
// when this framework is switched on (same gate as the TNFD metrics card).
export const FW = [
  { key: 'seea', group: 'Nature disclosure', name: 'UN SEEA Ecosystem account',
    ev: 'Extent and condition accounts informed by the Land Health Score and the regime-extent map.',
    status: 'baseline', note: 'Baseline established · extent and condition read from the published account' },
  { key: 'tnfd', group: 'Nature disclosure', name: 'TNFD · LEAP + core metrics',
    ev: 'LEAP locate-evaluate-assess-prepare run end-to-end; 7 of 14 core disclosure metrics derived directly from satellite.',
    status: 'evidenced', note: 'Indicative · decision-support, not VVB-verified' },
  { key: 'esrs_e4', group: 'Nature disclosure', name: 'CSRD · ESRS E4',
    ev: 'E4-5 ecosystem extent & condition metrics provided; E4-4 targets seeded from the 8-year condition trajectory.',
    status: 'evidenced', note: 'Maps to limited-assurance datapoints · gap log attached' },
  { key: 'gri101', group: 'Nature disclosure', name: 'GRI 101 · Biodiversity (2024)',
    ev: '101-5/6 extent and 101-7/8 condition & ecosystem services reported at site scale.',
    status: 'evidenced', note: 'Management-reported · ecointel-evidenced' },
  { key: 'sbtn', group: 'Nature disclosure', name: 'SBTN · Targets for Nature',
    ev: 'Step 1 assess & Step 2 interpret-prioritise built from condition and 8-year land-use change.',
    status: 'baseline', note: 'Baseline established · targets not yet validated by SBTN' },
  { key: 'issb', group: 'Nature disclosure', name: 'ISSB · IFRS S1 (+ nature guidance)',
    ev: 'Nature-related risks & dependencies positioned against S1 governance, strategy and risk-management.',
    status: 'positioned', note: 'Positioned for narrative · quantitative metrics pending' },
  { key: 'carbon', group: 'Carbon & climate', name: 'Carbon · GHG LSR / ISO 14064 / ISSB S2',
    ev: 'Land-sector carbon balance with SOC & AGB stocks; flux estimated with σ-corroboration across layers.',
    status: 'baseline', note: 'Baseline established · not VVB-verified' },
  { key: 'esrs_e1', group: 'Carbon & climate', name: 'CSRD · ESRS E1 (climate)',
    ev: 'Climate-context layers (temperature, rainfall, ET trends, drought, brittleness) plus land-sector removals give an indicative E1 backdrop.',
    status: 'baseline', note: 'Baseline established · client emissions inventory required' },
  { key: 'eudr', group: 'Regulatory readiness', name: 'EUDR · deforestation-free (Dec 2026)',
    ev: 'Geolocation polygons plus 8-year land-use change, no conversion detected after the cut-off date.',
    status: 'baseline', note: 'Baseline established · due-diligence statement not yet filed' },
];

// Short labels (journey-rail sublabels).
export const DISC_SHORT = {
  seea: 'UN SEEA',
  tnfd: 'TNFD', esrs_e4: 'ESRS E4', gri101: 'GRI 101', sbtn: 'SBTN',
  issb: 'ISSB', carbon: 'Carbon', esrs_e1: 'ESRS E1', eudr: 'EUDR',
};

// TNFD metric statuses: [fg, bg, label]. NOTE the order differs from ST (fg first).
export const METRIC_ST = {
  provided: ['#3B6D11', '#EAF3DE', 'Provided'],
  partial: ['#854F0B', '#FAEEDA', 'Partial'],
  none: ['#777', '#eee', 'Not assessed'],
};

// TNFD core metrics, in array order. Only `flagship` shows the big value.
// VALUES here are the FALLBACK; live values come from summary.disclosure.frameworks tnfd.metrics.
export const TM = [
  { cat: 'State of nature', name: 'Ecosystem extent', status: 'provided', value: '25.9 ha · 7 fields',
    note: 'Property by ecosystem type (land use), as recorded, with begin/end land-cover change. The SEEA and TNFD extent metric.' },
  { cat: 'State of nature', name: 'Ecosystem condition', flagship: true, status: 'provided', value: 'Land Health 5.4 · 4 processes',
    note: 'The UN SEEA ecosystem-condition account: five of six condition characteristics, scored against an ecoregion reference. The metric TNFD leaves open.' },
  { cat: 'State of nature', name: 'Species extinction risk', status: 'none', value: 'Not assessed',
    note: 'Needs an eDNA or species survey, out of scope this cycle. Ecological Community Dynamics (CDI) assesses community function as part of ecosystem condition, not a species count or extinction-risk metric.' },
  { cat: 'Drivers & pressures', name: 'Land & water-use change', status: 'provided', value: '4 detections · 8 yr',
    note: 'Change detection vs 2016 baseline; conversion flagged per parcel.' },
  { cat: 'Drivers & pressures', name: 'Climate change', status: 'provided', value: '+1.8 tCO₂e/ha/yr',
    note: 'Carbon balance plus climate-exposure context layers.' },
  { cat: 'Drivers & pressures', name: 'Resource & water use', status: 'partial', value: 'WCI 84%',
    note: 'Water-cycle proxy modelled; abstraction volumes not yet ingested.' },
  { cat: 'Drivers & pressures', name: 'Pollution', status: 'none', value: 'Not assessed',
    note: 'No effluent or nutrient-runoff monitoring on site.' },
  { cat: 'Drivers & pressures', name: 'Invasive species', status: 'none', value: 'Not assessed',
    note: 'Not mapped, requires a ground survey.' },
  { cat: 'Interface', name: 'Risk & opportunity', status: 'partial', value: 'per-parcel register',
    note: 'Dependency & physical-risk screen complete; financial materiality and the opportunity pipeline are still pending.' },
];
export const TM_CAT_ORDER = ['State of nature', 'Drivers & pressures', 'Interface'];

// Handoff 60: the six UN SEEA condition characteristics mapped to EcoIntel.
// Verbatim from prototype/disclosure.html (SEEA_CHARS). RB does not publish
// this as data yet; replace with RB's rows when it does.
export const SEEA_CHARACTERISTICS = [
  ['Physical (abiotic)', 'Water Cycle index + terrain'],
  ['Chemical (abiotic)', 'Mineral Cycle index'],
  ['Compositional (biotic)', 'Community Dynamics, a structural-diversity proxy (not species)'],
  ['Structural (biotic)', 'Energy Flow + biomass & canopy'],
  ['Functional (biotic)', 'All four processes + a productivity check'],
  ['Landscape', 'Ecoregion, terrain, field & habitat pattern'],
];

// Handoff 60: lead line on the UN SEEA account card. Fixed copy. The card
// emphasises the phrase "ecosystem-condition account".
export const SEEA_ACCOUNT_LEAD = 'EcoIntel maps onto the UN System of Environmental-Economic Accounting (SEEA), the international statistical standard the disclosure frameworks below (TNFD, ESRS E4, GRI 101) are built on. The Land Health Score is a SEEA ecosystem-condition account; the regime-extent map is its extent account. Extent and condition are two of the three state-of-nature metrics. Assess once here, and it maps into each framework below.';

export const EXTENT_LENS_ID = 'ecosystem_regime_extent';

const fmtHa = (v) => (Number.isFinite(Number(v)) ? Number(v).toFixed(1) : null);

function isExtentBlock(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/**
 * UN SEEA extent block (RB a39ff2a). Handoff 60 named surface.disclosure;
 * the ingestion allowlist forwards the top-level disclosure object
 * (rb.disclosure → summary.disclosure), and the extent block rides on that
 * object as summary.disclosure.ecosystem_extent. A payload that also lifted
 * the same block onto summary.ecosystemExtent is used only when the
 * disclosure copy is absent.
 */
export function publishedEcosystemExtent(summary) {
  if (!summary || typeof summary !== 'object') return null;
  const fromDisclosure = summary.disclosure && summary.disclosure.ecosystem_extent;
  if (isExtentBlock(fromDisclosure)) return fromDisclosure;
  if (isExtentBlock(summary.ecosystemExtent)) return summary.ecosystemExtent;
  return null;
}

/**
 * Handoff 60: display rows for RB's `ecosystem_extent.current[]` (recorded land
 * use). Values are RB's; only formatting happens here. Rows without a group or
 * area are dropped.
 */
export function extentByType(extent) {
  const rows = extent && Array.isArray(extent.current) ? extent.current : [];
  return rows
    .map((r) => ({
      group: r && r.group ? String(r.group) : null,
      ha: fmtHa(r && (r.area_ha != null ? r.area_ha : r.ha)),
      pct: r && Number.isFinite(Number(r.pct)) ? Number(r.pct) : null,
      fields: r && Number.isFinite(Number(r.field_count)) ? Number(r.field_count) : null,
    }))
    .filter((r) => r.group && r.ha != null);
}

/**
 * Handoff 60: fill the TNFD "Ecosystem extent" box from RB's ecosystem_extent
 * block (recorded land use governs). Leaves the curated box untouched when the
 * block is absent.
 */
export function applyEcosystemExtent(metrics, extent) {
  const rows = extentByType(extent);
  if (!rows.length) return metrics;
  const total = fmtHa(extent.total_ha);
  const types = rows.map((r) => `${r.group} ${r.ha} ha`).join(', ');
  const fields = rows.every((r) => r.fields != null) ? rows.reduce((s, r) => s + r.fields, 0) : null;
  return metrics.map((m) => (m.name !== 'Ecosystem extent' ? m : {
    ...m,
    status: 'provided',
    showValue: true,
    value: total != null ? `${total} ha${fields ? ` · ${fields} fields` : ''} · by type` : m.value,
    note: `Property by ecosystem type (land use), as recorded: ${types}. The SEEA and TNFD extent metric.`,
  }));
}

// Report once -> map to many. Left-border colour per row index.
export const DISC_ROUTE = [
  ['Condition · Land Health + 4 processes', ['SEEA condition', 'TNFD condition', 'ESRS E4-5', 'GRI 101-7/8', 'SBTN Step 1', 'ISSB']],
  ['Extent + 8-yr land-use change', ['SEEA extent', 'TNFD extent', 'EUDR geolocation', 'GRI 101-5/6', 'SBTN Land']],
  ['Carbon balance + SOC/AGB stocks', ['ISSB S2', 'GHG Protocol LSR', 'ISO 14064', 'ESRS E1']],
  ['Condition trajectory', ['ESRS E4-4 target', 'TNFD transition plan', 'GRI 101-2 restore']],
];
export const ROUTE_COLOURS = ['#3E9345', '#2E7BC4', '#3a3833', '#019DAD'];

// Hand-over cards (CARD 7) = the RB-UX handoverView three surfaces, verbatim:
// Supplier Credential, Assurance-readiness dossier, EUDR geolocation pack. VVB is
// NOT a card (EcoIntel never verifies) - it renders as the optional-external caveat
// line under the grid (VVB_CAVEAT below). cta wired to real packages in the app
// (cred / dossier / eudr); representational until wired. Reconciled 2026-07-02 to
// the RB-UX source of truth (handoff 10, commit f49036c): the earlier third card
// "VVB verification / Find a verifier" was a surface the RB-UX does not have, and it
// dropped the EUDR pack it does; the credential CTA "Produce credential" became
// "Open credential" (the app surfaces the RB-produced credential, never produces one).
// `action` drives the wired CTA (handoff 10 §6): 'cred' opens the RB-produced
// Supplier Credential PDF, 'dossier' downloads the assurance-readiness dossier
// (a `dossier` kind on the same `cred` package), 'eudr' assembles + downloads the
// EUDR pack client-side (§7b). All owner-gated via the report-download endpoint.
export const HANDOVER_CARDS = [
  { eyebrow: 'DOWN-CHAIN', title: 'Supplier Credential', action: 'cred',
    body: 'The bit you hand a supply-chain buyer: only EcoIntel\'s own evidence, carrying the seven safeguards. Safeguarded by construction, not assurance.',
    cta: 'Open credential' },
  { eyebrow: 'ASSEMBLE', title: 'Assurance-readiness dossier', action: 'dossier',
    body: 'Your evidence, substantiation pointers, operator attestations and pipeline commit, assembled so a VVB can assess. Prepared, not assured; not an IFRS S1 statement of compliance.',
    cta: 'Download the dossier (.json)' },
  { eyebrow: 'EUDR · DUE DILIGENCE', title: 'EUDR geolocation pack', action: 'eudr',
    body: 'Per-parcel geolocation and the 8-year change screen (geolocation is the one datapoint EcoIntel proves under Art. 9), plus a due-diligence assembly checklist and blank legal scaffolds. The no-conversion test, risk-tier and EU filing stay yours.',
    cta: 'Download the EUDR pack (.json)' },
];

// VVB is an optional-external caveat LINE under the hand-over grid, never a card and
// never a PASS/conformity badge (EcoIntel prepares; it never assures).
export const VVB_CAVEAT = 'Independent VVB verification is an optional, external step beyond EcoIntel\'s ceiling (Evidenced / Documented). Where a buyer or scheme requires market-grade assurance, the client hands the dossier to an accredited verifier. EcoIntel prepares; it never assures.';

// Method & honesty rows (CARD 8), in display order.
export const METHOD_ROWS = [
  ['evidenced', 'Backed by verifiable, satellite-derived evidence for this property.'],
  ['documented', 'A Baseline framework after you attest a management representation with evidence on file. Counts toward assessed, never promoted to Evidenced.'],
  ['positioned', 'Mapped and TNFD-aligned, narrative-ready, but quantitative metrics are still pending.'],
  ['baseline', 'A baseline is assembled and liftable; not yet evidenced.'],
  ['none', 'Out of scope this cycle, stated honestly, never padded.'],
];

// The disclosure inputs schema (CARD 6), ported from the RB UX disclosureInputsView.
// Each field is addressed by a dotted `disc` path into the client-held inputs store.
// `ro: true` = read-only EcoIntel-supplied evidence (the operator cannot edit it).
// `feeds` = the clause tag shown beside the label. Framework codes stay.
// An EcoIntel-supplied field uses a plain source label, never an internal
// score token. `tmpl: true` = an Insert-template button (R2).
export const DISC_SECTIONS = [
  { h: 'Materiality & horizons', fields: [
    { label: 'Materiality lens', disc: 'materiality', type: 'select', default: 'double', feeds: 'TNFD · ESRS',
      options: [['single', 'Single (financial)'], ['double', 'Double (impact + financial)']] },
    { label: 'Short-term', disc: 'horizons.short', ph: '≤3 yr' },
    { label: 'Medium-term', disc: 'horizons.medium', ph: '3 to 10 yr' },
    { label: 'Long-term', disc: 'horizons.long', ph: '≥10 yr' },
  ] },
  { h: 'Governance', fields: [
    { label: 'Board oversight of nature', disc: 'governance.board_oversight', type: 'textarea',
      feeds: 'TNFD Gov A · ESRS GOV', tmpl: true, ph: 'Who, how often, what decisions' },
    { label: "Management's role", disc: 'governance.management_role', type: 'textarea',
      feeds: 'TNFD Gov B', tmpl: true, ph: 'Accountable owner; how nature is managed' },
  ] },
  { h: 'Condition target', auto: 'baseline auto-read from EcoIntel', fields: [
    { label: 'Baseline (Land Health %)', disc: 'targets.0.baseline', type: 'number', ro: true, feeds: 'auto-read from EcoIntel' },
    { label: 'Target (Land Health %)', disc: 'targets.0.target_value', type: 'number', feeds: 'ESRS E4-4 · SBTN Step 3', ph: 'e.g. 65' },
    { label: 'Due', disc: 'targets.0.due', feeds: 'ESRS E4-4', ph: 'e.g. 2030' },
  ] },
  { h: 'Transition plan', auto: 'TNFD Nov-2025 five themes', fields: [
    { label: 'Plan summary', disc: 'transition_plan', type: 'textarea', rows: 3, tmpl: true,
      feeds: 'TNFD Prepare · ESRS E4-1', ph: 'Foundations · Implementation · Engagement · Metrics & Targets · Governance' },
  ] },
  { h: 'Policies', fields: [
    { label: 'Biodiversity / ecosystem policy', disc: 'policies.biodiversity', type: 'textarea', rows: 2, tmpl: true,
      feeds: 'GRI 101-1 · ESRS E4-2', ph: 'Reference your policy and date, or use the template to draft one' },
    { label: 'No-deforestation policy', disc: 'policies.no_deforestation', type: 'textarea', rows: 2, tmpl: true,
      feeds: 'EUDR', ph: 'Reference your policy and date, or use the template to draft one' },
  ] },
  { h: 'Operational emissions', wide: true,
    auto: 'your data. EcoIntel supplies the land-sector removals, never your operational figures',
    foot: 'A net carbon account needs your operational emissions. EcoIntel provides the indicative land-sector removals and never picks an emission factor or boundary, nor computes the net figure as Evidenced.',
    fields: [
      { label: 'Reporting year', disc: 'emissions.year', feeds: 'ESRS E1 · GHG Protocol', ph: 'e.g. 2025' },
      { label: 'Scope 1 · direct (tCO2e)', disc: 'emissions.scope1', type: 'number', feeds: 'ESRS E1-6', ph: 'fuel, livestock' },
      { label: 'Scope 2 · energy (tCO2e)', disc: 'emissions.scope2', type: 'number', feeds: 'ESRS E1-6', ph: 'electricity, heat' },
      { label: 'Scope 3 · value chain (tCO2e)', disc: 'emissions.scope3', type: 'number', feeds: 'ESRS E1-6 · ISSB S2', ph: 'optional' },
      { label: 'Energy use (MWh)', disc: 'emissions.energy_mwh', type: 'number', feeds: 'ESRS E1-5', ph: 'total' },
    ] },
];

// R2 readiness-ladder templates: EcoIntel frames the scaffold, the client authors it.
// A template is a management representation the operator writes; it is never satellite
// evidence. Keyed by the field's `disc` path. Verbatim from the RB UX DISC_TMPL.
export const DISC_TMPL = {
  'governance.board_oversight': 'The board reviews nature-related risks and opportunities [annually] as part of [name the committee or forum]. It oversees [the land-health strategy, the transition plan and target progress] and is informed by EcoIntel’s Land Health assessment and field reports. Decisions taken this period: [list them].',
  'governance.management_role': '[Role or named owner] is accountable for nature-related performance day to day, reporting to [the board or committee]. Nature is managed through [the practices, e.g. grazing planning and ground-cover management], monitored via EcoIntel Land Health and field observation, and reviewed [quarterly].',
  'transition_plan': 'Foundations: [the baseline condition and the ambition].\nImplementation: [the interventions and their timeline].\nEngagement: [advisers, buyers and community].\nMetrics and targets: [the Land Health target and carbon].\nGovernance: [oversight and the review cadence].',
  'policies.biodiversity': '[Organisation] commits to maintaining and improving ecosystem condition across the holding, measured by the Land Health Score, with no net loss of habitat extent. Adopted [month/year], reviewed [annually], owner [role].',
  'policies.no_deforestation': '[Organisation] commits to no conversion of woodland or priority habitat after [the cut-off date]. Land-use change is monitored via EcoIntel. Adopted [month/year].',
};

// ---- helpers -------------------------------------------------------------

// TNFD metric key -> curated card name, so live keyed metrics map onto the
// curated shells while labels/notes/categories stay curated.
const TM_KEY = {
  ecosystem_condition_index: 'Ecosystem condition',
  ecosystem_extent: 'Ecosystem extent',
};

// Merge the published per-site disclosure block over the curated FW metadata,
// then apply any client-held Lift attestations. `published` = summary.disclosure
// (may be null/undefined). `lifts` = the client lift store for this site:
// { [fwKey]: { rep, evidence, at } }. Returns one row per FW entry (including
// UN SEEA) with live status applied, plus a per-key live-metrics map for the
// TNFD card.
//
// A stored lift applies ONLY while the row is still baseline. It can never
// downgrade genuine evidence, nor upgrade a positioned row to documented.
export function resolveFrameworks(published, lifts) {
  const byId = {};
  (published && Array.isArray(published.frameworks) ? published.frameworks : []).forEach((f) => {
    byId[f.id] = f;
  });
  const L = lifts || {};
  return FW.map((r) => {
    const live = byId[r.key];
    let status = live && ST[live.status] ? live.status : r.status;
    let rep = null;
    let evidence = [];
    let note = r.note;
    if (status === 'baseline' && L[r.key]) {
      status = 'documented';
      rep = L[r.key].rep;
      evidence = L[r.key].evidence || [];
      note = 'Documented · operator-attested with evidence on file';
    }
    return { ...r, status, note, rep, evidence, lift: status === 'baseline', metrics: (live && live.metrics) || null };
  });
}

// Resolve the TNFD metric cards: prefer live values from the published tnfd entry,
// else the curated TM. Live metrics are keyed; we map them onto the curated card
// shells so labels/notes/categories stay curated.
export function resolveTnfdMetrics(rows) {
  const tnfd = rows.find((r) => r.key === 'tnfd');
  const liveByName = {};
  if (tnfd && Array.isArray(tnfd.metrics)) {
    tnfd.metrics.forEach((m) => {
      const name = TM_KEY[m.key] || m.label;
      if (name) liveByName[name] = m;
    });
  }
  return TM.map((m) => {
    const live = liveByName[m.name];
    if (!live) return m;
    const value =
      live.value != null
        ? `${live.unit ? `${live.unit} ` : ''}${live.value}${live.band ? ` · ${live.band}` : ''}`.trim()
        : m.value;
    return { ...m, value };
  });
}

export function disclosureCounts(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const c = { evidenced: 0, documented: 0, positioned: 0, baseline: 0, none: 0, total: list.length };
  list.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
  return c;
}

// Client-held framework selection on disclosureInputs (Type B preference).
// Missing `frameworksChosenAt` = the property admin has not yet saved a choice
// (opt-in empty). An explicit empty `enabledFrameworks` after a save is "none
// on". Portfolio settings will override this property-level choice later.
export function readFrameworkSelection(inputs) {
  const chosenAt = inputs && typeof inputs.frameworksChosenAt === 'string'
    ? inputs.frameworksChosenAt
    : null;
  const raw = inputs && Array.isArray(inputs.enabledFrameworks) ? inputs.enabledFrameworks : [];
  const known = new Set(FW.map((f) => f.key));
  const enabledKeys = raw.filter((k) => known.has(k));
  return {
    choiceMade: !!(chosenAt && chosenAt.length),
    enabledKeys,
    chosenAt,
  };
}

// Who sees which rows, and which rows feed % / pills.
// - Admin, not yet chosen: all rows as a greyed preview; counts stay empty (0%).
// - Assigned user, not yet chosen: every framework read-only so the page is not blank.
// - After a saved choice: only switched-on frameworks, for everyone.
export function applyFrameworkVisibility(rows, selection, canEdit) {
  const list = Array.isArray(rows) ? rows : [];
  const choiceMade = !!(selection && selection.choiceMade);
  const enabledKeys = (selection && selection.enabledKeys) || [];
  if (!choiceMade) {
    if (canEdit) {
      return { displayRows: list, countRows: [], preview: true, showAllReadonly: false };
    }
    return { displayRows: list, countRows: list, preview: false, showAllReadonly: true };
  }
  const filtered = list.filter((r) => enabledKeys.includes(r.key));
  return { displayRows: filtered, countRows: filtered, preview: false, showAllReadonly: false };
}

export function isFrameworkShowing(visibility, key) {
  if (!visibility || visibility.preview) return false;
  return (visibility.displayRows || []).some((r) => r.key === key);
}

// Map a "report once, map to many" clause label onto a curated FW key.
export function frameworkKeyForClause(label) {
  const s = String(label || '').toLowerCase();
  if (s.includes('seea')) return 'seea';
  if (s.includes('eudr')) return 'eudr';
  if (s.includes('esrs e1')) return 'esrs_e1';
  if (s.includes('esrs e4') || s.includes('esrs gov') || s.includes('esrs')) return 'esrs_e4';
  if (s.includes('gri')) return 'gri101';
  if (s.includes('sbtn')) return 'sbtn';
  if (s.includes('tnfd')) return 'tnfd';
  if (s.includes('ghg') || s.includes('iso 14064')) return 'carbon';
  if (s.includes('issb s2')) return 'carbon';
  if (s.includes('issb')) return 'issb';
  return null;
}

export function filterRoutePairs(visibility) {
  if (!visibility || visibility.preview) return [];
  const active = new Set((visibility.displayRows || []).map((r) => r.key));
  return DISC_ROUTE.map(([input, clauses]) => {
    const kept = clauses.filter((c) => {
      const key = frameworkKeyForClause(c);
      return !key || active.has(key);
    });
    return [input, kept];
  }).filter((pair) => pair[1].length);
}

export function fieldFeedsFrameworks(feeds) {
  const s = String(feeds || '');
  if (!s) return [];
  const keys = [];
  if (/tnfd/i.test(s)) keys.push('tnfd');
  if (/esrs\s*e1/i.test(s)) keys.push('esrs_e1');
  if (/esrs/i.test(s) && !/esrs\s*e1/i.test(s)) keys.push('esrs_e4');
  if (/gri/i.test(s)) keys.push('gri101');
  if (/sbtn/i.test(s)) keys.push('sbtn');
  if (/eudr/i.test(s)) keys.push('eudr');
  if (/ghg|iso 14064|carbon/i.test(s)) keys.push('carbon');
  if (/issb/i.test(s)) keys.push('issb');
  return keys;
}

export function filterDiscSections(visibility) {
  if (!visibility || visibility.preview) return [];
  const active = new Set((visibility.displayRows || []).map((r) => r.key));
  return DISC_SECTIONS.map((sec) => {
    const fields = (sec.fields || []).filter((f) => {
      const keys = fieldFeedsFrameworks(f.feeds);
      if (!keys.length) return true;
      return keys.some((k) => active.has(k));
    });
    return { ...sec, fields };
  }).filter((sec) => sec.fields.length);
}

export function filterHandoverCards(visibility) {
  if (!visibility || visibility.preview) {
    return HANDOVER_CARDS.filter((c) => c.action !== 'eudr');
  }
  const eudrOn = isFrameworkShowing(visibility, 'eudr');
  return HANDOVER_CARDS.filter((c) => c.action !== 'eudr' || eudrOn);
}

export function buildDisclosureActions(rows) {
  const c = disclosureCounts(rows);
  const items = [];
  if (c.baseline) {
    items.push(`<b>${c.baseline} at Baseline.</b> Attach a management representation + evidence to reach Documented.`);
  }
  if (c.positioned) {
    items.push(`<b>${c.positioned} Positioned.</b> Provide the client input the framework needs.`);
  }
  if (c.documented) {
    items.push(`<b>${c.documented} Documented.</b> Operator-attested with evidence on file.`);
  }
  if (c.evidenced) {
    items.push(`<b>${c.evidenced} Evidenced.</b> Evidence on file, ready to share.`);
  }
  if (!items.length && c.total) {
    items.push('No disclosure actions outstanding on the frameworks that are switched on.');
  }
  return {
    items,
    total: c.total,
    note: 'Lifts are managed on this tab; each attaches evidence and moves a framework toward Evidenced.',
  };
}

// readinessPct: EcoIntel ceiling = Evidenced + Documented = 100%. positioned 0.6,
// baseline 0.4. DERIVED, never hard-coded.
export function readinessPct(c) {
  if (!c.total) return 0;
  const w = c.evidenced * 1 + c.documented * 1 + c.positioned * 0.6 + c.baseline * 0.4;
  return Math.round((w / c.total) * 100);
}

// progressPct: per-row, stable jitter so bars read individually (Evidenced stays a
// clean 100). Replicate the exact formula so bars match the prototype.
export function progressPct(r) {
  const BASE = { evidenced: 100, documented: 95, positioned: 60, baseline: 42 };
  const base = BASE[r.status] != null ? BASE[r.status] : 30;
  let h = 0;
  for (let i = 0; i < r.key.length; i += 1) h += r.key.charCodeAt(i);
  const jit = r.status === 'evidenced' ? 0 : ((h % 6) - 2);
  return Math.max(8, Math.min(100, base + jit));
}

// Short framework names for a given status, joined with the middot.
export function shortsForStatus(rows, status) {
  return rows.filter((r) => r.status === status).map((r) => DISC_SHORT[r.key] || r.key).join(' · ');
}

// getDeep/setDeep for dotted disc paths (setDeep makes arrays for numeric
// segments). Used by InputsCard + the inputs store client.
export function getDeep(obj, path) {
  return path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
}
export function setDeep(obj, path, val) {
  const parts = path.split('.');
  let o = obj;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const k = parts[i];
    const nextNumeric = /^\d+$/.test(parts[i + 1]);
    if (o[k] == null) o[k] = nextNumeric ? [] : {};
    o = o[k];
  }
  o[parts[parts.length - 1]] = val;
  return obj;
}
